import isUndefined from 'lodash/isUndefined';

import { create } from '../../../shared/utils/dom';
import { WITHDRAWN_LABEL } from './constants';
import { SP_ONLY_CODE } from '../../../shared/components/sport-event/constants';
import Selection from '../../../shared/components/selection/Selection';
import { selectionsContainerDataList, selectionsList } from './app';
import i18n from '~shared/utils/i18n';
import { buildSpectateStaticAssetUrl } from '~spa/Utils/Spectate';
import {
    FEATURED_PAGE,
    NEXT_RACES_PAGE,
} from '~scenes/racingwidget/constants';
import { MEETING_PAGE } from '~spa/Pages/Racing/schedule-widget/constants';
import { POPULAR_BETS_PAGE } from '~scenes/betfeed/constants';

/**
 * Return selection object with some event information.
 * @param {Object} selection Event selection data
 * @param {Object} eventDetails event detailsdata
 * @return {Object} updated selection with some of the eventdata
 */
export const updateSelectionWithEventDetails = (selection, eventDetails) => {
    const {
        event_type: eventType,
        details: {
            is_antepost: eventIsAntepost = false,
        } = {},
    } = eventDetails;

    const isAntepost = eventType === 'ANTEPOST' || eventIsAntepost === '1' || eventIsAntepost === true;

    return {
        ...selection,
        isAntepost,
    };
};

/**
 * Return display Sp value.
 * @param {String} decimalPrice decimalPrice
 * @param {Boolean} spOnly selectionData spOnly value
 * @param {Boolean} tradable selectionData tradable value
 * @param {Boolean} active selectionData active value
 * @param {String} sportSlug selectionData sportSlug value
 * @param {Boolean} isAntepost selection belongs to a antepost event
 * @return {Boolean} spDisplay
 */
const getDisplaySp = (decimalPrice, spOnly, tradable, active, sportSlug, isAntepost) => {

    if(sportSlug === 'trotting' || (sportSlug === 'horse-racing' && isAntepost)){
        return parseFloat(decimalPrice) <= 0 || spOnly || !active;
    }

    return parseFloat(decimalPrice) <= 0 || spOnly || !tradable || !active;
};

/**
 * Converts selection data to initial value object accepted by Selection component.
 * @param {Object} selectionData selectionData
 * @param {Object} eventData eventData
 * @return {Object} selectionState selectionState
 */
export const normalizeStateForSelection = (selectionData) => {
    let decimalPrice = '0.00';
    const active = selectionData.active === '1' || selectionData.active === true;
    const tradable = selectionData.tradable === '1' || selectionData.tradable === true;
    const spOnly = (selectionData.sp_only === '1' || selectionData.sp_only === true);
    const { isAntepost = false } = selectionData;

    if (selectionData.decimal_price) {
        decimalPrice = selectionData.decimal_price;
    } else if (selectionData.current_price) {
        decimalPrice = selectionData.current_price;
    }

    const displaySP = getDisplaySp(decimalPrice, spOnly, tradable, active, selectionData.sport_slug, isAntepost);

    /**
     * The parameters are different based on the channel, a page refresh returns different data
     * compared to a Web socket message.
     *
     * 'decimal_price' and 'current_price' values vary but the sp_only is unique. Based on that
     * statement the sp_only would be the driven parameter to display the SP state in the selection.
     */
    if (displaySP) {
        decimalPrice = SP_ONLY_CODE;
    }

    const selectionState = {
        betable: active && tradable || displaySP,
        decimal_price: decimalPrice,
        event_id: selectionData.event_id,
        fraction_price: selectionData.fraction_price,
        id: selectionData.id || selectionData.selection_id,
        market_id: selectionData.market_id,
        name: selectionData.name,
        selection_db_name: selectionData.name,
        selection_schema: selectionData.selection_schema ? selectionData.selection_schema : null,
        selection_type: selectionData.selection_type_id ? selectionData.selection_type_id : null,
        sp_only: displaySP,
        specialoddsvalue: selectionData.specialoddsvalue ? selectionData.specialoddsvalue : null,
        suspended: selectionData.suspended ? selectionData.suspended : !displaySP && (active && !tradable),
        visible: !(selectionData.visible === '0' || !selectionData.visible),
        setWithdrawn: selectionData.outcome === 'VOID' ? true : !active && !tradable,
    };

    return selectionState;
};

/**
 * Create and append an HTML element with a text and classname
 * defined in the parameters
 * @param {HTMLElement} selector HTML element
 * @param {string} text Label of the element
 * @param {string} className Classname used for the element
 * @param {int} id id of the selection used to identify withdrawn element
 * @param {Function} onCreate Callback when the element is created
 * @return {void}
 */
export const createWithdrawnEl = (selector, text, className, id, onCreate) => {
    if (selector) {
        const existingWithdrawEl = selector.querySelector(`.${WITHDRAWN_LABEL}-${id}`);
        if (!existingWithdrawEl) {
            const withdrawnEl = create('span', className, text);
            selector.appendChild(withdrawnEl);

            if (onCreate && typeof onCreate === 'function') {
                onCreate(selector, id);
            }
        }
    }
};
/**
 * Remove an HTML element from the selector based on the classname
 * @param {HTMLElement} selector HTML element
 * @param {string} className Classname of the element
 * @return {void}
 */
export const removeWithdrawnEl = (selector, className) => {
    let withdrawnEl;

    if (selector) {
        withdrawnEl = selector.querySelector(`.${className}`);
    }

    if (withdrawnEl) {
        selector.removeChild(withdrawnEl);
    }
};

/**
 * Update previous odds under selection button
 *
 * @param {int} selectionId selection id
 * @param {int|String} oldSelectionPrice int previous price
 * @return {boolean} return true|false
 */
export const updatePreviousOdds = (selectionId, oldSelectionPrice) => {
    const limit = 2;
    const zero = 0;
    const one = 1;
    let oldPrice;

    if (!oldSelectionPrice || oldSelectionPrice === SP_ONLY_CODE) {
        return false;
    }

    if (typeof oldSelectionPrice === 'string') {
        oldPrice = oldSelectionPrice.trim();
    } else {
        oldPrice = oldSelectionPrice;
    }

    const prevOddsContainer = document.querySelector(`#HorseRacingYellowstoneRacecard__markets-selections__previous-odds_${selectionId}`);

    if (prevOddsContainer) {
        const prices = prevOddsContainer.querySelectorAll('span');

        if (prices.length === zero) {
            prevOddsContainer.innerHTML = `<span>${oldPrice}</span>`;
        } else {
            const lastPrice = prices[prices.length - one].innerText.trim();
            if (lastPrice !== oldPrice) {

                if (prices.length >= limit) {
                    prices[zero].remove();
                }
                const node = document.createElement('span');
                node.innerText = `${oldPrice}`;
                prevOddsContainer.appendChild(node);
            }
        }
    }

    return true;
};

/**
 * It generates selection buttons and add them to button element in DOM
 *
 * @param {object} betButtonEl HTML element
 * @param {Object} selectionData selection data
 * @param {String} context context of data origin
 * @param {String} gtmSource source for gtm click event
 * @return {void}
 */
export const generateButtons = (betButtonEl, selectionData, context, gtmSource) => {
    const id = parseInt(selectionData.id, 10);
    if(selectionData.setWithdrawn){
        const selectorEl = `${WITHDRAWN_LABEL} ${WITHDRAWN_LABEL}-${id}`;
        createWithdrawnEl(betButtonEl, i18n.t('racing.withdrawn'), selectorEl, id);
    } else {
        const selectionCmp = new Selection({
            initialState: selectionData,
            gtmAttribute: `${context};${gtmSource};`,
        });
        selectionsContainerDataList.set(`${context}_${id}`, selectionData);
        selectionsList.set(`${context}_${id}`, selectionCmp);
        betButtonEl.appendChild(selectionCmp.el);
    }
};

/**
 * Generates the object to pass to the mainSocketClass::subscribeForUpdates()
 * function
 *
 * @param {Array} data The list of events
 * @returns {Object|null} The normalized events object, null if the data is not valid
 */
export const createWsSubscriptionData = (data) => {
    if (isUndefined(data.selections_details)) {
        return null;
    }

    const selectionsDetails = data.selections_details;

    return Object.keys(selectionsDetails).map(selectionId => ({
        event_id: selectionsDetails[selectionId].event_id,
        market_id: selectionsDetails[selectionId].market_id,
    }));
};

/**
 * Get the silk style for the specified jockey
 * @param {String} jockey_filename The jockey
 * @return {String} The silk style tag
 */
export const getSilksBackgroundInlineStyle = (jockey_filename) => {
    const defaultSilkPath = buildSpectateStaticAssetUrl('/client/shared/images/default_silk.png');
    const jockeySilkPath = buildSpectateStaticAssetUrl('/images/silks/');
    const silks = [defaultSilkPath];
    if (jockey_filename) {
        silks.unshift(`${jockeySilkPath}${jockey_filename}`);
    }

    return silks.map((path) => `url(${path})`).join(',');
};

/**
 * Get the silk/trap style for the specified selection
 * @param {String} sport The sport slug
 * @param {String} selectionInfo The selection information
 * @return {String} The silk/trap style tag
 */
export const getSilkOrTrapInlineStyle = (sport, selectionInfo) => {
    const defaultSilkPath = buildSpectateStaticAssetUrl('/client/shared/images/default_silk.png');
    const jockeySilkPath = buildSpectateStaticAssetUrl('/images/silks/');
    const trapPath = buildSpectateStaticAssetUrl('/client/shared/images/sports/greyhound-racing/');
    const silks = [defaultSilkPath];

    if (sport === 'horse-racing') {
        if (selectionInfo.jockey_filename) {
            silks.unshift(`${jockeySilkPath}${selectionInfo.jockey_filename}`);
        }

        return silks.map((path) => `url(${path})`).join(',');
    } else if (sport === 'greyhound-racing' && selectionInfo.trap_number) {
        return `url(${trapPath}trap-${selectionInfo.trap_number}.svg)`;
    }

    return null;
};

/**
 * SPIN can send tradable = 0 (e.g. when they are recalculating all markets if a horse withdraws). Because we expect a
 * price update shortly we make it SP instead of suspending it.
 * @see AM-1320
 * @param {string} type type of racing
 * @param {Object} selection current selection information
 * @returns {Boolean}
 **/
export const shouldWeDisplaySpIfNotTradable = (type, selection) => {
    const { tradable, sp_only } = selection;
    if (type === 'horse-racing') {
        const isTradable = (tradable === '1' || tradable === true);
        const spOnly = sp_only === '1' || sp_only === true;

        return spOnly || !isTradable;
    }

    return selection.sp_only;
};

/**
 * Checks if selection should be betable if not tradable
 * @param {string} type type of racing
 * @param {number} eventId current event id
 * @param {object} events the available events
 * @param {object} selection the current selection
 * @returns {boolean} whether selection is betable or not
 */
export const shouldSelectionBeBetableIfNotTradable = (type, eventId, events, selection) => {
    const { tradable, betable, suspended } = selection;
    const isAntepost = Boolean(parseInt(events[eventId].details.is_antepost, 10));

    if (suspended) {
        return !suspended;
    }

    if (type === 'trotting' || (type === 'horse-racing' && isAntepost)) {
        return betable && tradable;
    }

    return betable;
};

/***
 * Check if the current component is used on the racing page (i.e., not the home page widget)
 * @param {string} displayType display type - only defined if called from a racing page at the moment
 * @returns {boolean} if it's on Featured Page
 */
export const isIndividualPage = (displayType) => {

    return [
        FEATURED_PAGE,
        NEXT_RACES_PAGE,
        MEETING_PAGE,
        POPULAR_BETS_PAGE,
    ].includes(displayType);
};
